// src/lib/vitalThresholds.ts
// The one place OPD vital-sign flagging ranges are defined.
//
// Before this module the same numbers were copy-pasted into four screens
// (VitalsStage, PreConsultStage, StageSummaryView, PatientRecords) and had
// already drifted apart — PatientRecords flags BMI > 25 while the three
// visit-pipeline screens flag >= 30, so the same patient reads "elevated"
// on one screen and "normal" on another. See
// docs/HMS_V4_UI_MODERNIZATION_ASSESSMENT.md and the Pre-Consult wireframe's
// decision 5.
//
// IMPORTANT — these are ADVISORY DISPLAY HINTS, not a clinical rules engine.
// Unlike the IP module's `ip_alert_rules` (NEWS2-derived, tenant-configurable,
// each row carrying a required `clinical_basis`), these OPD numbers have no
// published source in this codebase and are not configurable. They exist to
// tint a tile amber and print the cut-off that caused it. Nothing routes,
// scores, or blocks on them, and every screen that shows one also prints the
// word "advisory" next to it (see ADVISORY_NOTE below).
//
// Consolidated 2026-09-08 per the Pre-Consult wireframe's decision 5, answered
// A ("show them, and consolidate the constants into one module", thresholds
// labelled advisory). The four screens had drifted apart on three values;
// each is resolved here, once:
//
//   BMI   — PatientRecords flagged > 25, the three pipeline screens >= 30.
//           Resolved to >= 25, the value the wireframe itself draws
//           ("27.1 ↑ >25") and WHO's overweight cut-off. This makes the three
//           pipeline screens flag MORE readings than before; that is the
//           intended effect of an advisory band, not a regression.
//   Temp  — PatientRecords flagged > 99, the others > 99.5. Resolved to 99.5
//           (majority, and the more conservative of the two).
//   BP    — PatientRecords used >= 140 / >= 90, the others > 140 / > 90.
//           Resolved to the majority's strict comparison.
//
// Systolic/diastolic/pulse/SpO2 bands are otherwise unchanged from what every
// screen already used. Changing any number here changes it everywhere, which
// is the point — this file is the single edit a clinical governance decision
// now needs.

export interface VitalCheck {
  /** Reading falls outside the advisory range — tint the tile. */
  warn: boolean;
  /** Short statement of the cut-off that fired, e.g. "↑ >140/90". Only set when `warn`. */
  hint?: string;
}

const OK: VitalCheck = { warn: false };

/** Printed wherever these ranges are shown, per decision 5's condition that
 * the thresholds be labelled advisory. Keep it on the same card as the tiles
 * — a cut-off with no such label reads as a diagnosis. */
export const ADVISORY_NOTE = 'Ranges shown are advisory only — not a diagnosis.';

export const VITAL_LIMITS = {
  bpSystolicHigh: 140,
  bpSystolicLow: 90,
  bpDiastolicHigh: 90,
  pulseHigh: 100,
  pulseLow: 60,
  tempHighF: 99.5,
  spo2Low: 95,
  bmiHigh: 25,
  bmiLow: 18.5,
} as const;

/** Coerces a form value (which is a string in every one of these screens) to a
 * number, treating blank/absent/non-numeric as "not recorded" rather than 0 —
 * a missing SpO₂ must not read as 0% and flag as critical. */
function num(v: string | number | null | undefined): number | null {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function checkBp(systolic: string | number | null | undefined, diastolic: string | number | null | undefined): VitalCheck {
  const sys = num(systolic);
  const dia = num(diastolic);
  if (sys == null && dia == null) return OK;
  if (sys != null && sys > VITAL_LIMITS.bpSystolicHigh) return { warn: true, hint: `↑ >${VITAL_LIMITS.bpSystolicHigh} systolic` };
  if (dia != null && dia > VITAL_LIMITS.bpDiastolicHigh) return { warn: true, hint: `↑ >${VITAL_LIMITS.bpDiastolicHigh} diastolic` };
  if (sys != null && sys < VITAL_LIMITS.bpSystolicLow) return { warn: true, hint: `↓ <${VITAL_LIMITS.bpSystolicLow} systolic` };
  return OK;
}

export function checkPulse(value: string | number | null | undefined): VitalCheck {
  const v = num(value);
  if (v == null) return OK;
  if (v > VITAL_LIMITS.pulseHigh) return { warn: true, hint: `↑ >${VITAL_LIMITS.pulseHigh} bpm` };
  if (v < VITAL_LIMITS.pulseLow) return { warn: true, hint: `↓ <${VITAL_LIMITS.pulseLow} bpm` };
  return OK;
}

export function checkTemp(value: string | number | null | undefined): VitalCheck {
  const v = num(value);
  if (v == null) return OK;
  return v > VITAL_LIMITS.tempHighF ? { warn: true, hint: `↑ >${VITAL_LIMITS.tempHighF}°F` } : OK;
}

export function checkSpo2(value: string | number | null | undefined): VitalCheck {
  const v = num(value);
  if (v == null) return OK;
  return v < VITAL_LIMITS.spo2Low ? { warn: true, hint: `↓ <${VITAL_LIMITS.spo2Low}%` } : OK;
}

export function checkBmi(value: string | number | null | undefined): VitalCheck {
  const v = num(value);
  if (v == null) return OK;
  if (v >= VITAL_LIMITS.bmiHigh) return { warn: true, hint: `↑ ≥${VITAL_LIMITS.bmiHigh}` };
  if (v < VITAL_LIMITS.bmiLow) return { warn: true, hint: `↓ <${VITAL_LIMITS.bmiLow}` };
  return OK;
}
